import React, { useRef } from "react";

import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { styled, useTheme } from "@mui/material/styles";

import { useDrawer } from "./DrawerContext";

const Main = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "open" && prop !== "drawerWidth" && prop !== "location",
})<{ open: boolean; drawerWidth: number; location: "left" | "right" }>(
  ({ theme, open, drawerWidth, location }) => ({
    flexGrow: 1,
    transition: theme.transitions.create(["margin", "width"], {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    width: `calc(100% - ${open ? drawerWidth : 0}px)`,
    overflow: "auto",
    height: "calc(100vh - 50px)",
    ...(location === "right"
      ? {
          marginRight: open ? drawerWidth : 0,
        }
      : {
          marginLeft: open ? drawerWidth : 0,
        }),
  }),
);

// Two visual variants:
//   subtle=false (default) — full-height grey bar + drop shadow. Original
//     behaviour, used by RubiREPORT / RubiALERT / DisplayAlert.
//   subtle=true            — invisible cursor target with a centered three-dot
//     indicator, matching `ResizableSidePanel`'s discreet affordance. Opt-in
//     for new call sites (e.g. RubiFEEDBACK).
const ResizableHandle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "subtle",
})<{ subtle?: boolean }>(({ theme, subtle }) =>
  subtle
    ? {
        width: "8px",
        cursor: "col-resize",
        height: "100%",
        position: "absolute",
        top: 0,
        zIndex: 1000,
        backgroundColor: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& > .resize-indicator": {
          display: "flex",
          flexDirection: "column",
          gap: 4,
          opacity: 0.3,
          pointerEvents: "none",
          transition: theme.transitions.create("opacity", {
            duration: theme.transitions.duration.short,
          }),
        },
        "&:hover > .resize-indicator": {
          opacity: 0.7,
        },
      }
    : {
        width: "10px",
        cursor: "col-resize",
        height: "100%",
        position: "absolute",
        top: 0,
        zIndex: 1000,
        backgroundColor: theme.palette.action.hover,
        boxShadow: theme.shadows[2],
      },
);

const ResizeDot = styled(Box)(({ theme }) => ({
  width: 3,
  height: 3,
  borderRadius: "50%",
  backgroundColor: theme.palette.action.active,
}));

const CloseButton = styled(Box)<{ location: "left" | "right" }>(
  ({ theme, location }) => ({
    cursor: "pointer",
    zIndex: 1200,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "48px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: location === "left" ? "4px 0 0 4px" : "0 4px 4px 0",
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      boxShadow: theme.shadows[4],
      transition: theme.transitions.create("background-color", {
        duration: theme.transitions.duration.short,
      }),
    },
    "& svg": {
      fontSize: "18px",
    },
  }),
);

const SlideOutDrawer = ({
  slideoutContent,
  width = 240,
  mainContent,
  slideoutLocation = "right",
  onDrawerClose,
  subtleResize = false,
  borderless = false,
}: {
  slideoutContent: React.ReactNode;
  width?: number;
  mainContent: React.ReactNode;
  slideoutLocation?: "left" | "right";
  onDrawerClose: React.MouseEventHandler<HTMLDivElement>;
  // Opt-in: replace the full-height grey resize bar with an invisible target
  // and a centered three-dot indicator (matches ResizableSidePanel).
  subtleResize?: boolean;
  // Opt-in: strip MUI Drawer's default 1px inside-edge border so the drawer
  // blends into the page surface.
  borderless?: boolean;
}) => {
  const { isOpen, closeDrawer, drawerWidth, setDrawerWidth } = useDrawer();
  const theme = useTheme();

  const handleRef = useRef<HTMLDivElement | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    const startX = e.clientX;
    const startWidth = drawerWidth;

    // should not select text while dragging
    e.preventDefault();

    const doDrag = (moveEvent: MouseEvent) => {
      const newWidth =
        startWidth +
        (slideoutLocation === "right"
          ? startX - moveEvent.clientX
          : moveEvent.clientX - startX);
      setDrawerWidth(
        Math.min(Math.max(newWidth, 200), window.innerWidth * 0.8),
      );
    };

    const stopDrag = () => {
      document.removeEventListener("mousemove", doDrag);
      document.removeEventListener("mouseup", stopDrag);
    };

    document.addEventListener("mousemove", doDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  const handleOffset = subtleResize ? "-4px" : "-2.5px";

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
      }}
    >
      <Main open={isOpen} drawerWidth={drawerWidth} location={slideoutLocation}>
        {mainContent}
      </Main>
      <Drawer
        variant="persistent"
        anchor={slideoutLocation}
        open={isOpen}
        PaperProps={{
          sx: {
            width: drawerWidth,
            position: "fixed",
            top: 0,
            [slideoutLocation]: 0,
            height: "calc(100vh)",
            marginTop: theme.spacing(6),
            overflow: "hidden",
            ...(borderless ? { border: "none" } : {}),
          },
        }}
      >
        <ResizableHandle
          ref={handleRef}
          subtle={subtleResize}
          onMouseDown={handleMouseDown}
          sx={{
            left: slideoutLocation === "right" ? handleOffset : undefined,
            right: slideoutLocation === "left" ? handleOffset : undefined,
          }}
        >
          {subtleResize && (
            <Box className="resize-indicator">
              <ResizeDot />
              <ResizeDot />
              <ResizeDot />
            </Box>
          )}
        </ResizableHandle>
        <Box
          sx={{
            paddingLeft: slideoutLocation === "right" ? 1 : -1,
            overflowY: "auto",
            height: "calc(100vh - 50px)",
          }}
        >
          {slideoutContent}
        </Box>
      </Drawer>
    </Box>
  );
};

export default SlideOutDrawer;
